import { memo } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { SkeletonBlock } from '../common/Skeleton.web';

const JOB_DETAIL_WEB_MAX_WIDTH = 820;

interface JobsSkeletonProps {
  variant?: 'feed' | 'detail';
  count?: number;
}

export const JobsSkeleton = memo(function JobsSkeleton({
  variant = 'feed',
  count = 4,
}: JobsSkeletonProps) {
  const { theme } = useTheme();

  if (variant === 'detail') {
    return (
      <div
        style={{
          maxWidth: JOB_DETAIL_WEB_MAX_WIDTH,
          marginInline: 'auto',
          width: '100%',
          paddingInline: 'var(--tabbio-job-detail-gutter, 16px)',
          paddingBlock: '20px 96px',
          display: 'grid',
          gap: 22,
        }}
      >
        <div
          style={{
            borderRadius: 24,
            border: `1px solid ${theme.borderSubtle}`,
            backgroundColor: theme.background,
            padding: 22,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                border: `1px solid ${theme.borderSubtle}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SkeletonBlock width={42} height={42} radius={10} />
            </div>
            <SkeletonBlock width={80} height={13} radius={999} />
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <SkeletonBlock width="76%" height={42} radius={18} />
            <SkeletonBlock width="52%" height={16} radius={999} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <SkeletonBlock width={112} height={32} radius={999} />
              <SkeletonBlock width={104} height={32} radius={999} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
            <SkeletonBlock width={92} height={34} radius={999} />
            <SkeletonBlock width={88} height={34} radius={999} />
            <SkeletonBlock width={78} height={34} radius={999} />
          </div>
          <div
            style={{
              marginTop: 18,
              borderRadius: 18,
              border: `1px solid ${theme.borderSubtle}`,
              backgroundColor: theme.background,
              padding: 16,
              display: 'grid',
              gap: 10,
            }}
          >
            <SkeletonBlock width="34%" height={16} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <SkeletonBlock width={118} height={28} radius={999} />
              <SkeletonBlock width={96} height={28} radius={999} />
            </div>
            <SkeletonBlock width="82%" height={13} />
          </div>
          <div style={{ marginTop: 18 }}>
            <SkeletonBlock width="100%" height={48} radius={999} />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <SkeletonBlock width="64%" height={12} radius={999} />
            </div>
          </div>
        </div>

        <DetailSkeletonPanel titleWidth={108} lineWidths={['100%', '94%', '88%', '62%']} />
        <DetailSkeletonListPanel titleWidth={122} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: 'var(--bg)',
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={`job-skeleton-${index}`}
          style={{
            borderBottom:
              index === count - 1 ? 'none' : `1px solid ${theme.borderSubtle}`,
            padding: '20px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            minHeight: 96,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              border: `1px solid ${theme.borderSubtle}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SkeletonBlock width={34} height={34} radius={8} />
          </div>

          <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 7 }}>
            <SkeletonBlock width="72%" height={18} radius={7} />
            <SkeletonBlock width="48%" height={14} radius={7} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <SkeletonBlock width={76} height={28} radius={999} />
              <SkeletonBlock width={88} height={28} radius={999} />
            </div>
            <SkeletonBlock width="42%" height={12} radius={999} />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <SkeletonBlock width={18} height={18} radius={999} />
            <SkeletonBlock width={18} height={18} radius={999} />
          </div>
        </div>
      ))}
    </div>
  );
});

function DetailSkeletonPanel({
  titleWidth,
  lineWidths,
}: {
  titleWidth: number;
  lineWidths: (number | string)[];
}) {
  return (
    <section>
      <SkeletonBlock width={titleWidth} height={18} radius={999} />
      <div
        style={{
          marginTop: 10,
          borderRadius: 22,
          border: '1px solid var(--tabbio-border-subtle)',
          backgroundColor: 'var(--bg)',
          padding: 18,
          display: 'grid',
          gap: 12,
        }}
      >
        {lineWidths.map((width, index) => (
          <SkeletonBlock key={`detail-panel-line-${index}`} width={width} height={14} />
        ))}
      </div>
    </section>
  );
}

function DetailSkeletonListPanel({ titleWidth }: { titleWidth: number }) {
  return (
    <section>
      <SkeletonBlock width={titleWidth} height={18} radius={999} />
      <div
        style={{
          marginTop: 10,
          borderRadius: 22,
          border: '1px solid var(--tabbio-border-subtle)',
          backgroundColor: 'var(--bg)',
          padding: '6px 18px',
        }}
      >
        {[0, 1, 2].map((index) => (
          <div
            key={`detail-list-line-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 46,
              borderBottom:
                index === 2 ? 'none' : '1px solid var(--tabbio-border-subtle)',
            }}
          >
            <SkeletonBlock width={24} height={20} radius={999} />
            <SkeletonBlock width={`${82 - index * 10}%`} height={15} />
          </div>
        ))}
      </div>
    </section>
  );
}
